import { ClientData } from "api/clientService";
import { ClientFlags, EClientData, IClient, IClientData } from "dto/IClient";

import type { IAddress, IData } from ".";

// Zapis podmiotu (PUT /client) ZASTĘPUJE komplet jego danych kontaktowych — wszystko, czego
// formularz nie odeśle, znika z bazy. Stąd: prefill z pełnych danych, typy nieedytowane w
// formularzu odsyłane bez zmian, a niezmienione wartości w pierwotnej postaci (inaczej każda
// edycja tworzyłaby nową wersję podmiotu i oznaczała przesyłki jako „Nowsze dane adresata”).

type Json = Record<string, unknown>;

/** Typy danych edytowane w formularzu; pozostałe (osoby kontaktowe, skrytka ePUAP…) wracają bez zmian. */
const EDITED_TYPES = new Set<string>([
  EClientData.ADDRESS,
  EClientData.EMAIL,
  EClientData.PHONE,
  EClientData.eaddress,
  EClientData.bank,
  EClientData.NOTE,
]);

const ADDRESS_KEYS = ["city", "zipCode", "address", "isHeadquarter"];

const pascal = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

const asObject = (raw?: string | null): Json | null => {
  try {
    const parsed: unknown = JSON.parse(raw ?? "");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Json) : null;
  } catch {
    return null;
  }
};

// BE zapisuje adres kluczami camelCase (city/zipCode/address); starsze wpisy bywają PascalCase.
const text = (json: Json, key: string) => {
  const value = json[key] ?? json[pascal(key)];
  return typeof value === "string" ? value : "";
};

export const parseAddress = (raw: string): IAddress | null => {
  const json = asObject(raw);
  if (!json) return null;
  return { street: text(json, "address"), zipCode: text(json, "zipCode"), city: text(json, "city"), raw };
};

/** Niezmieniony adres wraca w pierwotnej postaci; zmieniony zachowuje pozostałe pola z bazy. */
const addressValue = (address: IAddress, isMain: boolean) => {
  const original = address.raw ? parseAddress(address.raw) : null;
  if (
    original &&
    original.street === address.street &&
    original.zipCode === address.zipCode &&
    original.city === address.city
  ) {
    return address.raw as string;
  }

  const rest: Json = { ...(asObject(address.raw) ?? {}) };
  ADDRESS_KEYS.forEach((key) => {
    delete rest[key];
    delete rest[pascal(key)];
  });
  return JSON.stringify({
    ...rest,
    city: address.city,
    zipCode: address.zipCode,
    address: address.street,
    isHeadquarter: isMain,
  });
};

const atLeast = (values: string[], count: number) => [
  ...values,
  ...Array<string>(Math.max(0, count - values.length)).fill(""),
];

/** Formularz z pełnych danych podmiotu; wartość główna danego typu pierwsza (pole w formularzu). */
export const toFormData = (client: IClient, prev: IData): IData => {
  const rows: IClientData[] = client.Data ?? [];
  const ofType = (type: string) =>
    rows.filter((r) => r.Type === type).sort((a, b) => Number(b.IsMain) - Number(a.IsMain));
  const values = (type: string) => ofType(type).map((r) => r.Value ?? "");

  const addressRows = ofType(EClientData.ADDRESS).map((row) => ({ row, address: parseAddress(row.Value) }));
  const addresses = addressRows.flatMap((x) => (x.address ? [x.address] : []));
  const notes = ofType(EClientData.NOTE);

  const nip = client.NIP ?? "";
  const regon = client.REGON ?? "";

  return {
    ...prev,
    name: (client.Name ?? "").replaceAll("<em>", "").replaceAll("</em>", ""),
    nip,
    personNip: nip,
    regon,
    personRegon: regon,
    krs: client.KRS ?? null,
    pesel: "",
    hasSavedPesel: ((client.Flags ?? 0) & ClientFlags.HasPesel) !== 0,
    peselRemoved: false,
    emails: atLeast(values(EClientData.EMAIL), 2),
    phones: atLeast(values(EClientData.PHONE), 2),
    EAddresses: atLeast(values(EClientData.eaddress), 2),
    bank: atLeast(values(EClientData.bank), 1),
    addresses: addresses.length ? addresses : [{ city: "", street: "", zipCode: "", address: "" }],
    note: notes[0]?.Value ?? "",
    original: rows,
    // nieedytowane typy, kolejne notatki i adresy, których nie da się odczytać — bez zmian
    preserved: [
      ...rows.filter((r) => !EDITED_TYPES.has(r.Type)),
      ...notes.slice(1),
      ...addressRows.filter((x) => !x.address).map((x) => x.row),
    ],
  };
};

/** Dane kontaktowe do zapisu (komplet — zapis zastępuje wszystkie dane podmiotu). */
export const toClientData = (data: IData): ClientData[] => {
  const originalValues = new Set((data.original ?? []).map((r) => r.Value));
  // spacje usuwamy tylko z wartości wpisanych teraz — zapisana wcześniej zostaje jak była
  const compact = (value: string) => (originalValues.has(value) ? value : value.replaceAll(" ", ""));

  const list = (type: string, values: string[] | undefined, normalize: (v: string) => string = (v) => v) =>
    (values ?? [])
      .map((v) => normalize(v ?? ""))
      .filter((v) => v.trim().length > 0)
      .map((value, index) => ({ Type: type, Value: value, IsMain: index === 0 }));

  const addresses = (data.addresses ?? [])
    .filter((a) => [a.city, a.zipCode, a.street].some((v) => !!v?.trim()))
    .map((a, index) => ({ Type: EClientData.ADDRESS, Value: addressValue(a, index === 0), IsMain: index === 0 }));

  const contactPersons = (data.contactPerson ?? [])
    .filter((p) => [p.name, p.email, p.phone].some((v) => !!v?.trim()))
    .map((p, index) => {
      const [firstName = "", ...lastName] = (p.name ?? "").trim().split(" ");
      return {
        Type: EClientData.CONTACT_PERSON,
        Value: JSON.stringify({
          FirstName: firstName,
          LastName: lastName.join(" "),
          JobPosition: "",
          Phone: p.phone,
          Email: p.email,
        }),
        IsMain: index === 0,
      };
    });

  const note = data.note?.trim() ? [{ Type: EClientData.NOTE, Value: data.note, IsMain: true }] : [];

  return [
    ...addresses,
    ...list(EClientData.EMAIL, data.emails, compact),
    ...list(EClientData.PHONE, data.phones, compact),
    ...contactPersons,
    ...list(EClientData.eaddress, data.EAddresses),
    ...list(EClientData.bank, data.bank),
    ...(data.preserved ?? []).map((r) => ({ Type: r.Type, Value: r.Value, IsMain: r.IsMain })),
    ...note,
  ];
};
